import React from 'react'

import {
    LineChart,
    Line,
    AreaChart,
    Area,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Legend
} from 'recharts'

import { useQuery } from 'react-query'

import {
    useForm,
    Controller
} from 'react-hook-form'

import api from '../../../components/api'


export default function DashboardEvolucao(props) {

    const {
        adesao_institucional_id,
        anos = [],
        modo: modoDashboard = 'RELATORIO',
        exportandoPDF = false
    } = props


    const anosSelecionados =
        Array.isArray(anos)
            ? anos
                .map(item => Number(item))
                .filter(item => Number.isInteger(item))
            : []


    const anoSelecionado =
        anosSelecionados.length === 1
            ? anosSelecionados[0]
            : null


    // =========================================================
    // FORM
    // =========================================================

    const {
        control,
        watch,
        setValue
    } = useForm({

        defaultValues: {

            grupoResiduo_id: '',

            subGrupoResiduo_id: '',

            tipoTratamento_id: '',

            indicador: 'kg',

            modo: 'absoluto',

            tipoGrafico: 'linha'

        }

    })

    const indicador =
        watch('indicador')

    // =========================================================
    // FILTROS
    // =========================================================

    const grupoResiduo_id =
        watch('grupoResiduo_id')

    const subGrupoResiduo_id =
        watch('subGrupoResiduo_id')

    const tipoTratamento_id =
        watch('tipoTratamento_id')

    const modo =
        watch('modo')

    const tipoGrafico =
        watch('tipoGrafico')


    // =========================================================
    // GRUPOS
    //
    // GET grupoResid
    // =========================================================

    const {
        data: grupos = [],
        isLoading: loadingGrupos
    } = useQuery({

        queryKey: [
            'grupoResid'
        ],

        queryFn: async () => {

            const result =
                await api.get(
                    'grupoResid'
                )

            return result.data ?? []

        }

    })


    // =========================================================
    // SUBGRUPOS
    //
    // GET subGrupoResid/:grupoResiduo_id
    // =========================================================

    const {
        data: subgrupos = [],
        isLoading: loadingSubgrupos
    } = useQuery({

        queryKey: [
            'subGrupoResid',
            grupoResiduo_id
        ],

        queryFn: async () => {

            const result =
                await api.get(
                    `subGrupoResid/${grupoResiduo_id}`
                )

            return result.data ?? []

        },

        enabled:
            !!grupoResiduo_id

    })


    // =========================================================
    // TIPOS DE TRATAMENTO
    //
    // GET tipoTratamento/:subGrupoResiduo_id
    // =========================================================

    const {
        data: tratamentos = [],
        isLoading: loadingTratamentos
    } = useQuery(
        'tipoTratamentoTodosDashboard',
        async () => {

            const result =
                await api.get(
                    'tipoTratamentoTodos'
                )

            return result.data || []

        },
        {
            refetchOnWindowFocus: false,
            staleTime: 5 * 60 * 1000
        }
    )


    // =========================================================
    // DASHBOARD
    // =========================================================

    const {

        data,

        isLoading,

        isFetching,

        isError

    } = useQuery({

        queryKey: [

            'residuosDashboardEvolucao',

            adesao_institucional_id,

            anosSelecionados.join(','),

            grupoResiduo_id,

            subGrupoResiduo_id,

            tipoTratamento_id,

            indicador,

            modoDashboard

        ],

        queryFn: async () => {

            const result =
                await api.post(
                    'residuosDashboardEvolucao',
                    {

                        adesao_institucional_id:
                            Number(
                                adesao_institucional_id
                            ),

                        anos:
                            anosSelecionados,

                        grupoResiduo_id:
                            grupoResiduo_id
                                ? Number(
                                    grupoResiduo_id
                                )
                                : null,

                        subgrupoResiduo_id:
                            subGrupoResiduo_id
                                ? Number(
                                    subGrupoResiduo_id
                                )
                                : null,

                        tipoTratamento_id:
                            tipoTratamento_id
                                ? Number(
                                    tipoTratamento_id
                                )
                                : null,

                        indicador:
                            indicador,

                        modo:
                            modoDashboard

                    }
                )

            return result.data

        },

        enabled:
            !!adesao_institucional_id

    })

        // =========================================================
    // TABELA DE INDICADORES
    // =========================================================

    const {

        data: dataTabela,

        isLoading: isLoadingTabela,

        isFetching: isFetchingTabela,

        isError: isErrorTabela

    } = useQuery({

        queryKey: [

            'residuosDashboardEvolucaoTabela',

            adesao_institucional_id,

            anosSelecionados.join(','),

            grupoResiduo_id,

            subGrupoResiduo_id,

            tipoTratamento_id,

            modoDashboard,

        ],

        queryFn: async () => {

            const result =
                await api.post(
                    'residuosDashboardEvolucaoTabela',
                    {

                        adesao_institucional_id:
                            Number(
                                adesao_institucional_id
                            ),

                        anos:
                            anosSelecionados,

                        modo: modoDashboard,

                        grupoResiduo_id:
                            grupoResiduo_id
                                ? Number(
                                    grupoResiduo_id
                                )
                                : null,

                        subgrupoResiduo_id:
                            subGrupoResiduo_id
                                ? Number(
                                    subGrupoResiduo_id
                                )
                                : null,

                        tipoTratamento_id:
                            tipoTratamento_id
                                ? Number(
                                    tipoTratamento_id
                                )
                                : null

                    }
                )

            return result.data

        },

        enabled:
            !!adesao_institucional_id

    })


    // =========================================================
    // ALTERA GRUPO
    //
    // Ao trocar grupo:
    //
    // 1. altera grupo
    // 2. limpa subgrupo
    // 3. limpa tratamento
    // =========================================================

    const alteraGrupo = (
        field,
        valor
    ) => {

        field.onChange(valor)

        setValue(
            'subGrupoResiduo_id',
            ''
        )

    }


    // =========================================================
    // ALTERA SUBGRUPO
    //
    // Ao trocar subgrupo limpa o tratamento.
    // =========================================================

    const alteraSubgrupo = (
        field,
        valor
    ) => {

        field.onChange(valor)

    }


    // =========================================================
    // FORMATADORES
    // =========================================================

    const formataNumero = (valor) => {

        return Number(
            valor || 0
        ).toLocaleString(
            'pt-BR',
            {
                minimumFractionDigits: 1,
                maximumFractionDigits: 2
            }
        )

    }


    const formataPercentual = (valor) => {

        if (
            valor === null ||
            valor === undefined
        ) {

            return '—'

        }


        return `${
            Number(valor) > 0
                ? '+'
                : ''
        }${Number(valor).toLocaleString(
            'pt-BR',
            {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            }
        )}%`

    }


    // =========================================================
    // DADOS DO GRÁFICO
    // =========================================================

    const series =
        data?.series ?? []


    const tipoSerie =
        data?.tipoSerie || 'total'


    const multiplasSeries =
        data?.multiplasSeries === true


    // =========================================================
    // CORES DAS SÉRIES
    // =========================================================

    const coresSeries = [
        '#1683c7',
        '#28a745',
        '#dc3545',
        '#fd7e14',
        '#6f42c1',
        '#20c997',
        '#17a2b8',
        '#e83e8c',
        '#6c757d',
        '#ffc107'
    ]


    const getCorSerie = (index) => {

        return coresSeries[
            index % coresSeries.length
        ]

    }


    // =========================================================
    // MONTA OS DADOS NO FORMATO DO RECHARTS
    //
    // Exemplo:
    //
    // {
    //     periodo: 'Jan',
    //     serie_1: 193.8,
    //     serie_3: 0,
    //     serie_4: 1971.9,
    //     serie_5: 22.6
    // }
    //
    // =========================================================

    const dadosGrafico = []


if (series.length > 0) {

    const periodos = {}


    // =========================================================
    // PRIMEIRO MONTA OS VALORES ABSOLUTOS
    // =========================================================

    series.forEach(serie => {

        const dadosSerie =
            serie?.dados ?? []


        dadosSerie.forEach(item => {

            const chave =
                String(item.ordem)


            if (!periodos[chave]) {

                periodos[chave] = {

                    ordem:
                        Number(item.ordem),

                    periodo:
                        item.periodo,

                    totalPeriodo:
                        0

                }

            }


            const campo =
                `serie_${serie.id}`

            const campoPendencia =
                `serie_${serie.id}_pendencia`


            const valor =
                Number(
                    item.total || 0
                )

            const valorPendencia =
                Number(
                    item.totalPendencia || 0
                )


            periodos[chave][campo] =
                valor

            periodos[chave][campoPendencia] =
                valorPendencia


            periodos[chave].totalPeriodo +=
                valor

        })

    })


    // =========================================================
    // CONVERTE PARA RELATIVO QUANDO NECESSÁRIO
    // =========================================================

    Object
        .values(periodos)
        .sort(
            (a, b) =>
                Number(a.ordem) -
                Number(b.ordem)
        )
        .forEach(item => {

            const registro = {
                ...item
            }


            if (
                modo === 'relativo'
            ) {

                series.forEach(serie => {

                    const campo =
                        `serie_${serie.id}`

                    const campoPendencia =
                        `serie_${serie.id}_pendencia`


                    const valor =
                        Number(
                            registro[campo] || 0
                        )

                    const valorPendencia =
                        Number(
                            registro[campoPendencia] || 0
                        )


                    registro[campo] =
                        registro.totalPeriodo > 0

                            ? Number(
                                (
                                    (
                                        valor /
                                        registro.totalPeriodo
                                    ) *
                                    100
                                ).toFixed(2)
                            )

                            : 0

                    registro[campoPendencia] =
                        registro.totalPeriodo > 0

                            ? Number(
                                (
                                    (
                                        valorPendencia /
                                        registro.totalPeriodo
                                    ) *
                                    100
                                ).toFixed(2)
                            )

                            : 0

                })

            }


            dadosGrafico.push(
                registro
            )

        })

}


    // =========================================================
    // TÍTULO DO GRÁFICO
    // =========================================================

    const tituloGrafico = (() => {

        if (tipoSerie === 'grupo') {

            return 'Evolução por Grupo de Resíduos'

        }


        if (tipoSerie === 'subgrupo') {

            return 'Evolução por Subgrupo de Resíduos'

        }


        if (tipoSerie === 'tratamento') {

            return 'Evolução por Tratamento/Destinação'

        }


        return 'Evolução do recorte selecionado'

    })()



    // =========================================================
    // DADOS DA TABELA
    // =========================================================

    const dadosTabela =
        dataTabela?.dados ?? []



    // =========================================================
    // PLACAR
    // =========================================================

    const Placar = ({
        titulo,
        valor,
        subtitulo
    }) => {

        return (

            <div
                style={{
                    minHeight: '205px',

                    border:
                        '1px solid #b9dcf3',

                    borderRadius:
                        '14px',

                    backgroundColor:
                        '#eef8ff',

                    padding:
                        '25px 15px',

                    display:
                        'flex',

                    flexDirection:
                        'column',

                    alignItems:
                        'center',

                    justifyContent:
                        'center',

                    textAlign:
                        'center',

                    boxShadow:
                        '0 2px 8px rgba(0,0,0,0.04)'
                }}
            >

                <div
                    style={{
                        color:
                            '#1683c7',

                        fontSize:
                            '19px',

                        fontWeight:
                            '700',

                        marginBottom:
                            '20px'
                    }}
                >
                    {titulo}
                </div>


                <div
                    style={{
                        color:
                            '#222',

                        fontSize:
                            '38px',

                        fontWeight:
                            '700',

                        lineHeight:
                            '1.1'
                    }}
                >
                    {valor}
                </div>


                {subtitulo && (

                    <div
                        style={{
                            color:
                                '#777',

                            fontSize:
                                '12px',

                            marginTop:
                                '15px'
                        }}
                    >
                        {subtitulo}
                    </div>

                )}

            </div>

        )

    }


    // =========================================================
    // LOADING PRINCIPAL
    // =========================================================

    if (isLoading) {

        return (

            <div
                className="text-center"
                style={{
                    padding: '80px'
                }}
            >

                <i
                    className="fa fa-spinner fa-spin"
                    style={{
                        fontSize: '30px'
                    }}
                />

                <div
                    style={{
                        marginTop: '10px'
                    }}
                >
                    Carregando dashboard...
                </div>

            </div>

        )

    }


    // =========================================================
    // ERRO
    // =========================================================

    if (isError) {

        return (

            <div className="alert alert-danger">

                Não foi possível carregar os dados do dashboard.

            </div>

        )

    }



    const PontoLinha = ({
        cx,
        cy,
        payload,
        serieId,
        cor,
        ativo = false
    }) => {

        const pendencia =
            modoDashboard === 'CORRECAO' &&
            Number(
                payload?.[
                    `serie_${serieId}_pendencia`
                ] || 0
            ) > 0


        const raio =
            ativo
                ? 6
                : 4


        if (pendencia) {

            return (
                <g>

                    <circle
                        cx={cx}
                        cy={cy}
                        r={raio + 2}
                        fill="#ffffff"
                        stroke="#d97706"
                        strokeWidth={2}
                        strokeDasharray="3 2"
                    />

                    <circle
                        cx={cx}
                        cy={cy}
                        r={raio - 1}
                        fill={cor}
                        stroke="#ffffff"
                        strokeWidth={1}
                    />

                </g>
            )

        }


        return (
            <circle
                cx={cx}
                cy={cy}
                r={raio}
                fill={cor}
                stroke="#ffffff"
                strokeWidth={1}
            />
        )

    }



    return (

        <div>



            {/* =====================================================
                FILTROS
               ===================================================== */}

            <div
                style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e1e5e8',
                    borderRadius: '12px',
                    padding: '18px',
                    marginBottom: '20px'
                }}
            >

                <div className="row">


                    {/* =================================================
                        GRUPO
                       ================================================= */}

                    <div className="col-md-3">

                        <div className="form-group">

                            <label>
                                Grupo de resíduo
                            </label>


                            <Controller
                                name="grupoResiduo_id"
                                control={control}
                                render={({ field }) => (

                                    <select
                                        {...field}
                                        className="form-control"
                                        disabled={
                                            loadingGrupos
                                        }
                                        onChange={
                                            e =>
                                                alteraGrupo(
                                                    field,
                                                    e.target.value
                                                )
                                        }
                                    >

                                        <option value="">
                                            Todos
                                        </option>


                                        {grupos.map(
                                            rs => (

                                                <option
                                                    key={
                                                        rs.grupoResiduo_id
                                                    }
                                                    value={
                                                        rs.grupoResiduo_id
                                                    }
                                                >

                                                    {
                                                        rs.grupoResiduo_descri
                                                    }

                                                </option>

                                            )
                                        )}

                                    </select>

                                )}
                            />

                        </div>

                    </div>


                    {/* =================================================
                        SUBGRUPO
                       ================================================= */}

                    <div className="col-md-3">

                        <div className="form-group">

                            <label>
                                Subgrupo
                            </label>


                            <Controller
                                name="subGrupoResiduo_id"
                                control={control}
                                render={({ field }) => (

                                    <select
                                        {...field}
                                        className="form-control"
                                        disabled={
                                            !grupoResiduo_id ||
                                            loadingSubgrupos
                                        }
                                        onChange={
                                            e =>
                                                alteraSubgrupo(
                                                    field,
                                                    e.target.value
                                                )
                                        }
                                    >

                                        <option value="">

                                            {
                                                !grupoResiduo_id
                                                    ? 'Selecione o grupo'
                                                    : 'Todos'
                                            }

                                        </option>


                                        {subgrupos.map(
                                            rs => (

                                                <option
                                                    key={
                                                        rs.subGrupoResiduo_id
                                                    }
                                                    value={
                                                        rs.subGrupoResiduo_id
                                                    }
                                                >

                                                    {
                                                        rs.subGrupoResiduo_descri
                                                    }

                                                </option>

                                            )
                                        )}

                                    </select>

                                )}
                            />

                        </div>

                    </div>


                    {/* =================================================
                        TRATAMENTO / DESTINAÇÃO
                       ================================================= */}

                    <div className="col-md-3">

                        <div className="form-group">

                            <label>
                                Tratamento/Destinação
                            </label>


                            <Controller

                                name="tipoTratamento_id"

                                control={control}

                                render={({ field }) => (

                                    <select
                                        {...field}
                                        className="form-control"
                                        disabled={
                                            loadingTratamentos
                                        }
                                    >

                                        <option value="">
                                            Todos
                                        </option>

                                        {tratamentos.map(rs => (

                                            <option
                                                key={
                                                    rs.tipoTratamento_id
                                                }
                                                value={
                                                    rs.tipoTratamento_id
                                                }
                                            >
                                                {
                                                    rs.tipoTratamento_descri
                                                }
                                            </option>

                                        ))}

                                    </select>

                                )}
                            />

                        </div>

                    </div>


                    {/* =================================================
                        INDICADOR
                       ================================================= */}

                    <div className="col-md-3">

                        <div className="form-group">

                            <label>
                                Pesagem
                            </label>


                            <Controller
                                name="indicador"
                                control={control}
                                render={({ field }) => (

                                    <select
                                        {...field}
                                        className="form-control"
                                    >

                                        <option value="kg">
                                            Quilograma (kg)
                                        </option>

                                        <option value="kg_fte">
                                            kg/FTE
                                        </option>

                                        <option value="kg_paciente_dia">
                                            kg/Paciente Dia
                                        </option>

                                        <option value="kg_area">
                                            kg/Área construída
                                        </option>

                                    </select>

                                )}
                            />

                        </div>

                    </div>


                </div>

            </div>


            {/* =====================================================
                CABEÇALHO GRÁFICO
               ===================================================== */}

            <div
                className="d-flex justify-content-between align-items-center flex-wrap"
                style={{
                    marginBottom: '15px',
                    gap: '10px'
                }}
            >


            <div>

                <h4
                    style={{
                        margin: 0,
                        fontWeight: '600',
                        color: '#333'
                    }}
                >
                    {tituloGrafico}
                </h4>

                <div
                    style={{
                        fontSize: '13px',
                        color: '#777',
                        marginTop: '4px'
                    }}
                >
                    {data?.periodicidade === 'mensal' && anoSelecionado
                        ? `Evolução mensal em ${anoSelecionado}`
                        : anosSelecionados.length > 1
                            ? `Evolução anual • ${anosSelecionados.length} anos selecionados`
                            : 'Evolução anual'
                    }

                    {series.length > 1
                        ? ` • ${series.length} séries`
                        : ''
                    }
                </div>

            </div>
               


                {/* =================================================
                    BOTÕES
                   ================================================= */}

                <div
                    className="d-flex"
                    style={{
                        gap: '8px'
                    }}
                >


                    {/* ABSOLUTO / RELATIVO */}

                    <Controller
                        name="modo"
                        control={control}
                        render={({ field }) => (

                            <div className="btn-group">

                                <button
                                    type="button"
                                    className={
                                        field.value === 'absoluto'
                                            ? 'btn btn-primary'
                                            : 'btn btn-outline-primary'
                                    }
                                    onClick={() =>
                                        field.onChange(
                                            'absoluto'
                                        )
                                    }
                                >
                                    Absoluto
                                </button>


                                <button
                                    type="button"
                                    className={
                                        field.value === 'relativo'
                                            ? 'btn btn-primary'
                                            : 'btn btn-outline-primary'
                                    }
                                    onClick={() =>
                                        field.onChange(
                                            'relativo'
                                        )
                                    }
                                >
                                    Relativo
                                </button>

                            </div>

                        )}
                    />


                    {/* LINHA / ÁREA */}

                    <Controller
                        name="tipoGrafico"
                        control={control}
                        render={({ field }) => (

                            <div className="btn-group">

                                <button
                                    type="button"
                                    className={
                                        field.value === 'linha'
                                            ? 'btn btn-success'
                                            : 'btn btn-outline-success'
                                    }
                                    onClick={() =>
                                        field.onChange(
                                            'linha'
                                        )
                                    }
                                >
                                    Linha
                                </button>


                                <button
                                    type="button"
                                    className={
                                        field.value === 'area'
                                            ? 'btn btn-success'
                                            : 'btn btn-outline-success'
                                    }
                                    onClick={() =>
                                        field.onChange(
                                            'area'
                                        )
                                    }
                                >
                                    Área
                                </button>

                            </div>

                        )}
                    />

                </div>

            </div>


            {/* =====================================================
                PLACARES + GRÁFICO
               ===================================================== */}

            <div className="row">


                {/* =================================================
                    PLACARES
                   ================================================= */}

                <div className="col-md-3">


                    <Placar
                        titulo="Resultado"
                        valor={
                            `${formataNumero(
                                data?.resultado
                            )} ${data?.unidade || 'kg'}`
                        }
                        subtitulo={
                            anoSelecionado
                                ? `Total informado em ${anoSelecionado}`
                                : anosSelecionados.length > 1
                                    ? `Total dos ${anosSelecionados.length} anos selecionados`
                                    : 'Total do período selecionado'
                        }
                    />


                    <div
                        style={{
                            height: '20px'
                        }}
                    />


                    <Placar
                        titulo="Variação no período"
                        valor={
                            formataPercentual(
                                data?.variacaoPeriodo
                            )
                        }
                        subtitulo={
                            data?.variacao?.periodoInicial &&
                            data?.variacao?.periodoFinal
                                ? `${data.variacao.periodoInicial} → ${data.variacao.periodoFinal}`
                                : null
                        }
                    />


                </div>


                {/* =================================================
                    GRÁFICO
                   ================================================= */}

                <div className="col-md-9">

                    <div
                        style={{
                            height: '430px',
                            backgroundColor: '#ffffff',
                            border: '1px solid #d7dce1',
                            borderRadius: '14px',
                            padding: '20px',
                            position: 'relative',
                            boxShadow:
                                '0 2px 8px rgba(0,0,0,0.04)'
                        }}
                    >


                        {isFetching && (

                            <div
                                style={{
                                    position: 'absolute',
                                    top: '10px',
                                    right: '20px',
                                    fontSize: '12px',
                                    color: '#777',
                                    zIndex: 10
                                }}
                            >

                                <i
                                    className="fa fa-spinner fa-spin"
                                    style={{
                                        marginRight: '5px'
                                    }}
                                />

                                Atualizando...

                            </div>

                        )}


                        {
                            dadosGrafico.length === 0

                                ? (

                                    <div
                                        style={{
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#777'
                                        }}
                                    >
                                        Nenhum dado disponível para os filtros selecionados.
                                    </div>

                                )

                                : (

                                    <ResponsiveContainer
                                            width="100%"
                                            height="100%"
                                        >

                                            {
                                                tipoGrafico === 'area'

                                                    ? (

                                                        <AreaChart
                                                            data={dadosGrafico}
                                                            margin={{
                                                                top: 20,
                                                                right: 25,
                                                                left: 20,
                                                                bottom: 15
                                                            }}
                                                        >


                                                            <defs>

                                                                {series.map(
                                                                    (serie, index) => (

                                                                        <pattern
                                                                            key={
                                                                                `pendencia-area-${serie.id}`
                                                                            }
                                                                            id={
                                                                                `pendencia-area-${serie.id}`
                                                                            }
                                                                            width="8"
                                                                            height="8"
                                                                            patternUnits="userSpaceOnUse"
                                                                            patternTransform="rotate(45)"
                                                                        >

                                                                            <rect
                                                                                width="8"
                                                                                height="8"
                                                                                fill="rgba(255,255,255,0.65)"
                                                                            />

                                                                            <line
                                                                                x1="0"
                                                                                y1="0"
                                                                                x2="0"
                                                                                y2="8"
                                                                                stroke={
                                                                                    getCorSerie(index)
                                                                                }
                                                                                strokeWidth="3"
                                                                            />

                                                                        </pattern>

                                                                    )
                                                                )}



                                                            </defs>

                                                            <CartesianGrid
                                                                strokeDasharray="3 3"
                                                            />


                                                            <XAxis
                                                                dataKey="periodo"
                                                            />


                                                            <YAxis
                                                                tickFormatter={
                                                                    value =>
                                                                        modo === 'relativo'
                                                                            ? `${formataNumero(value)}%`
                                                                            : Number(
                                                                                value
                                                                            ).toLocaleString(
                                                                                'pt-BR'
                                                                            )
                                                                }
                                                            />


                                                            <Tooltip
                                                                formatter={(
                                                                    value,
                                                                    name,
                                                                    props
                                                                ) => {

                                                                    if (
                                                                        String(name).startsWith(
                                                                            'pendencia_'
                                                                        )
                                                                    ) {
                                                                        return null
                                                                    }

                                                                    const serie =
                                                                        series.find(
                                                                            item =>
                                                                                `serie_${item.id}` ===
                                                                                name
                                                                        )


                                                                    const nomeSerie =
                                                                        serie?.nome ||
                                                                        name

                                                                    const serieId =
                                                                        serie?.id


                                                                    const valorPendencia =
                                                                        serieId
                                                                            ? Number(
                                                                                props?.payload?.[
                                                                                    `serie_${serieId}_pendencia`
                                                                                ] || 0
                                                                            )
                                                                            : 0


                                                                    const possuiPendencia =
                                                                        modoDashboard === 'CORRECAO' &&
                                                                        valorPendencia > 0


                                                                    const unidade =
                                                                        data?.unidade || 'kg'


                                                                    const valorFormatado =
                                                                        modo === 'relativo'
                                                                            ? `${formataNumero(value)}%`
                                                                            : `${formataNumero(value)} ${unidade}`


                                                                    const pendenciaFormatada =
                                                                        modo === 'relativo'
                                                                            ? `${formataNumero(
                                                                                valorPendencia
                                                                            )}%`
                                                                            : `${formataNumero(
                                                                                valorPendencia
                                                                            )} ${unidade}`


                                                                    return [
                                                                        possuiPendencia
                                                                            ? `${valorFormatado} — ${pendenciaFormatada} com pendência`
                                                                            : valorFormatado,

                                                                        nomeSerie
                                                                    ]

                                                                }}
                                                                labelFormatter={
                                                                    label =>
                                                                        `Período: ${label}`
                                                                }
                                                            />


                                                            <Legend
                                                                formatter={value => {

                                                                    const serie =
                                                                        series.find(
                                                                            item =>
                                                                                `serie_${item.id}` ===
                                                                                value
                                                                        )


                                                                    return (
                                                                        serie?.nome ||
                                                                        value
                                                                    )

                                                                }}
                                                            />


                                                            {series.map(
                                                                (serie, index) => (

                                                                    <Area
                                                                        key={serie.id}

                                                                        type="monotone"

                                                                        dataKey={
                                                                            `serie_${serie.id}`
                                                                        }

                                                                        name={
                                                                            `serie_${serie.id}`
                                                                        }

                                                                        stroke={
                                                                            getCorSerie(index)
                                                                        }

                                                                        fill={
                                                                            getCorSerie(index)
                                                                        }

                                                                        fillOpacity={
                                                                            multiplasSeries
                                                                                ? 0.55
                                                                                : 0.25
                                                                        }

                                                                        strokeWidth={2}

                                                                        stackId={
                                                                            multiplasSeries
                                                                                ? 'total'
                                                                                : undefined
                                                                        }

                                                                        connectNulls={true}
                                                                        isAnimationActive={!exportandoPDF}
                                                                    />

                                                                )
                                                            )}



                                                            {modoDashboard === 'CORRECAO' &&
                                                                series.map(
                                                                    (serie, index) => (

                                                                        <Area
                                                                            key={
                                                                                `pendencia-${serie.id}`
                                                                            }

                                                                            type="monotone"

                                                                            dataKey={
                                                                                `serie_${serie.id}_pendencia`
                                                                            }

                                                                            name={
                                                                                `pendencia_${serie.id}`
                                                                            }

                                                                            stroke={
                                                                                getCorSerie(index)
                                                                            }

                                                                            strokeDasharray="5 4"

                                                                            strokeWidth={2}

                                                                            fill={
                                                                                `url(#pendencia-area-${serie.id})`
                                                                            }

                                                                            fillOpacity={0.8}

                                                                            connectNulls={true}

                                                                            legendType="none"

                                                                            isAnimationActive={!exportandoPDF}
                                                                        />

                                                                    )
                                                                )
                                                            }

                                                        </AreaChart>


                                                    )

                                                    : (

                                                        <LineChart
                                                            data={dadosGrafico}
                                                            margin={{
                                                                top: 20,
                                                                right: 25,
                                                                left: 20,
                                                                bottom: 15
                                                            }}
                                                        >



                                                            <CartesianGrid
                                                                strokeDasharray="3 3"
                                                            />


                                                            <XAxis
                                                                dataKey="periodo"
                                                            />


                                                            <YAxis
                                                                tickFormatter={
                                                                    value =>
                                                                        modo === 'relativo'
                                                                            ? `${formataNumero(value)}%`
                                                                            : Number(
                                                                                value
                                                                            ).toLocaleString(
                                                                                'pt-BR'
                                                                            )
                                                                }
                                                            />


                                                            <Tooltip
                                                                formatter={(
                                                                    value,
                                                                    name,
                                                                    props
                                                                ) => {

                                                                    const serie =
                                                                        series.find(
                                                                            item =>
                                                                                `serie_${item.id}` ===
                                                                                name
                                                                        )

                                                                    const nomeSerie =
                                                                        serie?.nome ||
                                                                        name

                                                                    const serieId =
                                                                        serie?.id

                                                                    const valorPendencia =
                                                                        serieId
                                                                            ? Number(
                                                                                props?.payload?.[
                                                                                    `serie_${serieId}_pendencia`
                                                                                ] || 0
                                                                            )
                                                                            : 0

                                                                    const possuiPendencia =
                                                                        modoDashboard === 'CORRECAO' &&
                                                                        valorPendencia > 0

                                                                    const unidade =
                                                                        data?.unidade || 'kg'

                                                                    const valorFormatado =
                                                                        modo === 'relativo'
                                                                            ? `${formataNumero(value)}%`
                                                                            : `${formataNumero(value)} ${unidade}`

                                                                    const pendenciaFormatada =
                                                                        modo === 'relativo'
                                                                            ? `${formataNumero(
                                                                                valorPendencia
                                                                            )}%`
                                                                            : `${formataNumero(
                                                                                valorPendencia
                                                                            )} ${unidade}`

                                                                    return [
                                                                        possuiPendencia
                                                                            ? `${valorFormatado} — ${pendenciaFormatada} com pendência`
                                                                            : valorFormatado,

                                                                        nomeSerie
                                                                    ]

                                                                }}
                                                                labelFormatter={
                                                                    label =>
                                                                        `Período: ${label}`
                                                                }
                                                            />


                                                            <Legend
                                                                formatter={value => {

                                                                    const serie =
                                                                        series.find(
                                                                            item =>
                                                                                `serie_${item.id}` ===
                                                                                value
                                                                        )


                                                                    return (
                                                                        serie?.nome ||
                                                                        value
                                                                    )

                                                                }}
                                                            />


                                                            {series.map(
                                                                (serie, index) => (

                                                                    <Line
                                                                        key={
                                                                            serie.id
                                                                        }

                                                                        type="monotone"

                                                                        dataKey={
                                                                            `serie_${serie.id}`
                                                                        }

                                                                        name={
                                                                            `serie_${serie.id}`
                                                                        }

                                                                        stroke={
                                                                            getCorSerie(index)
                                                                        }

                                                                        strokeWidth={3}

                                                                        dot={props => (
                                                                            <PontoLinha
                                                                                {...props}
                                                                                serieId={serie.id}
                                                                                cor={
                                                                                    getCorSerie(index)
                                                                                }
                                                                        isAnimationActive={!exportandoPDF}
                                                                            />
                                                                        )}

                                                                        activeDot={props => (
                                                                            <PontoLinha
                                                                                {...props}
                                                                                serieId={serie.id}
                                                                                cor={
                                                                                    getCorSerie(index)
                                                                                }
                                                                                ativo
                                                                            />
                                                                        )}

                                                                        connectNulls={true}
                                                                    />

                                                                )
                                                            )}

                                                        </LineChart>

                                                    )
                                            }

                                        </ResponsiveContainer>

                                )
                        }

                    </div>

                </div>

                            </div>


            {/* =====================================================
                TABELA DE INDICADORES
               ===================================================== */}

            <div
                style={{
                    marginTop: '30px'
                }}
            >

                <div
                    className="d-flex justify-content-between align-items-center"
                    style={{
                        marginBottom: '12px'
                    }}
                >

                    <div>

                        <h4
                            style={{
                                margin: 0,
                                fontWeight: '600',
                                color: '#333'
                            }}
                        >
                            Indicadores de geração de resíduos
                        </h4>

                        <div
                            style={{
                                fontSize: '13px',
                                color: '#777',
                                marginTop: '4px'
                            }}
                        >

                            {anoSelecionado
                                ? `Indicadores referentes ao ano de ${anoSelecionado}`
                                : anosSelecionados.length > 1
                                    ? `Comparativo dos ${anosSelecionados.length} anos selecionados`
                                    : 'Comparativo dos indicadores por ano'
                            }

                        </div>

                    </div>


                    {isFetchingTabela && (

                        <div
                            style={{
                                fontSize: '12px',
                                color: '#777'
                            }}
                        >

                            <i
                                className="fa fa-spinner fa-spin"
                                style={{
                                    marginRight: '5px'
                                }}
                            />

                            Atualizando...

                        </div>

                    )}

                </div>


                <div
                    style={{
                        border:
                            '1px solid #d7dce1',

                        borderRadius:
                            '10px',

                        overflow:
                            'hidden',

                        backgroundColor:
                            '#ffffff',

                        boxShadow:
                            '0 2px 8px rgba(0,0,0,0.04)'
                    }}
                >

                    {isLoadingTabela ? (

                        <div
                            className="text-center"
                            style={{
                                padding: '40px'
                            }}
                        >

                            <i
                                className="fa fa-spinner fa-spin"
                                style={{
                                    fontSize: '24px'
                                }}
                            />

                            <div
                                style={{
                                    marginTop: '10px',
                                    color: '#777'
                                }}
                            >
                                Carregando indicadores...
                            </div>

                        </div>

                    ) : isErrorTabela ? (

                        <div
                            className="alert alert-danger"
                            style={{
                                margin: '15px'
                            }}
                        >
                            Não foi possível carregar a tabela de indicadores.
                        </div>

                    ) : dadosTabela.length === 0 ? (

                        <div
                            className="text-center"
                            style={{
                                padding: '35px',
                                color: '#777'
                            }}
                        >
                            Nenhum indicador disponível para os filtros selecionados.
                        </div>

                    ) : (

                        <div
                            className="table-responsive"
                            style={{
                                margin: 0
                            }}
                        >

                            <table
                                className="table table-bordered table-hover"
                                style={{
                                    marginBottom: 0,
                                    whiteSpace: 'nowrap'
                                }}
                            >

                                <thead>

                                    <tr>

                                        <th
                                            className="text-center"
                                            style={{
                                                backgroundColor: '#0d477e',
                                                color: '#ffffff',
                                                verticalAlign: 'middle'
                                            }}
                                        >
                                            Ano
                                        </th>

                                        <th
                                            className="text-center"
                                            style={{
                                                backgroundColor: '#0d477e',
                                                color: '#ffffff',
                                                verticalAlign: 'middle'
                                            }}
                                        >
                                            Resíduos
                                            <br />
                                            (kg)
                                        </th>

                                        <th
                                            className="text-center"
                                            style={{
                                                backgroundColor: '#0d477e',
                                                color: '#ffffff',
                                                verticalAlign: 'middle'
                                            }}
                                        >
                                            FTE
                                        </th>

                                        <th
                                            className="text-center"
                                            style={{
                                                backgroundColor: '#0d477e',
                                                color: '#ffffff',
                                                verticalAlign: 'middle'
                                            }}
                                        >
                                            Leitos
                                        </th>

                                        <th
                                            className="text-center"
                                            style={{
                                                backgroundColor: '#0d477e',
                                                color: '#ffffff',
                                                verticalAlign: 'middle'
                                            }}
                                        >
                                            Salas
                                        </th>

                                        <th
                                            className="text-center"
                                            style={{
                                                backgroundColor: '#0d477e',
                                                color: '#ffffff',
                                                verticalAlign: 'middle'
                                            }}
                                        >
                                            Área (m²)
                                        </th>

                                        <th
                                            className="text-center"
                                            style={{
                                                backgroundColor: '#0d477e',
                                                color: '#ffffff',
                                                verticalAlign: 'middle'
                                            }}
                                        >
                                            kg/FTE
                                        </th>

                                        <th
                                            className="text-center"
                                            style={{
                                                backgroundColor: '#0d477e',
                                                color: '#ffffff',
                                                verticalAlign: 'middle'
                                            }}
                                        >
                                            kg/leito
                                        </th>

                                        <th
                                            className="text-center"
                                            style={{
                                                backgroundColor: '#0d477e',
                                                color: '#ffffff',
                                                verticalAlign: 'middle'
                                            }}
                                        >
                                            kg/sala
                                        </th>

                                        <th
                                            className="text-center"
                                            style={{
                                                backgroundColor: '#0d477e',
                                                color: '#ffffff',
                                                verticalAlign: 'middle'
                                            }}
                                        >
                                            kg/m²
                                        </th>

                                    </tr>

                                </thead>


                                <tbody>

                                    {dadosTabela.map(
                                        (item) => (

                                            <tr
                                                key={
                                                    item.ano
                                                }
                                            >

                                                <td
                                                    className="text-center"
                                                    style={{
                                                        fontWeight: '600'
                                                    }}
                                                >
                                                    {item.ano}
                                                </td>


                                                <td className="text-right">

                                                    {formataNumero(
                                                        item.residuos
                                                    )}

                                                </td>


                                                <td className="text-right">

                                                    {formataNumero(
                                                        item.fte
                                                    )}

                                                </td>


                                                <td className="text-right">

                                                    {formataNumero(
                                                        item.leitos
                                                    )}

                                                </td>


                                                <td className="text-right">

                                                    {formataNumero(
                                                        item.salas
                                                    )}

                                                </td>


                                                <td className="text-right">

                                                    {formataNumero(
                                                        item.area
                                                    )}

                                                </td>


                                                <td className="text-right">

                                                    {
                                                        item.kgFte !== null &&
                                                        item.kgFte !== undefined

                                                            ? formataNumero(
                                                                item.kgFte
                                                            )

                                                            : '—'
                                                    }

                                                </td>


                                                <td className="text-right">

                                                    {
                                                        item.kgLeito !== null &&
                                                        item.kgLeito !== undefined

                                                            ? formataNumero(
                                                                item.kgLeito
                                                            )

                                                            : '—'
                                                    }

                                                </td>


                                                <td className="text-right">

                                                    {
                                                        item.kgSala !== null &&
                                                        item.kgSala !== undefined

                                                            ? formataNumero(
                                                                item.kgSala
                                                            )

                                                            : '—'
                                                    }

                                                </td>


                                                <td className="text-right">

                                                    {
                                                        item.kgM2 !== null &&
                                                        item.kgM2 !== undefined

                                                            ? formataNumero(
                                                                item.kgM2
                                                            )

                                                            : '—'
                                                    }

                                                </td>

                                            </tr>

                                        )
                                    )}

                                </tbody>

                            </table>

                        </div>

                    )}

                </div>

            </div>


        </div>

    )

}